import {Breadcrumbs} from '@primer/react'
import {clsx} from 'clsx'
import {useGlobalNavAnalytics} from '../../utils/Analytics'
import {getOcticon, type OcticonName} from '../../utils/octicons'
import type {LocalNavigationTab} from '../../types'
import styles from './styles.module.css'
import {useTopNavContext} from '../../primitives/TopNav'
import {FeatureFlags} from '@primer/react/experimental'

export interface ContextCrumb {
  crumb_type: string
  label: string
  is_root: boolean
  octicon?: OcticonName
  path?: string
  href?: string
}

export interface ContextRegionPayload {
  /** Array of breadcrumb items for the context region */
  crumbs: ContextCrumb[]
  /** Array of local navigation tabs (e.g., for repository navigation) */
  localNavigation?: LocalNavigationTab[]
  /** A string identifier that indicates what links should be active according to the route's controller */
  selectedLink?: string
}

export interface ContextRegionProps {
  crumbs?: ContextCrumb[]
}

export function ContextRegion({crumbs}: ContextRegionProps) {
  const {sendItemClick} = useGlobalNavAnalytics('CONTEXT_REGION')
  const {searchOpen, isSearchPage} = useTopNavContext()

  if (!crumbs || crumbs.length === 0 || isSearchPage || searchOpen) {
    return null
  }

  return (
    <FeatureFlags flags={{primer_react_breadcrumbs_overflow_menu: true}}>
      {/*
        ESLint thinks that the `overflow` prop is a system prop, but it's not.
        See https://github.com/github/primer/issues/6101
      */}

      {/* eslint-disable-next-line primer-react/no-system-props */}
      <Breadcrumbs overflow="menu">
        {crumbs.map((crumb, index) => {
          const href = crumb.href || crumb.path
          const tagType = href ? 'a' : 'span'
          const isLast = index === crumbs.length - 1
          const Octicon = getOcticon(crumb.octicon)

          return (
            <Breadcrumbs.Item
              key={href || crumb.label}
              href={href}
              as={tagType}
              className={clsx({
                [styles.contextCrumb]: true,
                [styles.contextCrumbStatic]: tagType === 'span',
              })}
              onClick={() => {
                sendItemClick('CONTEXT_REGION_CRUMB', `position:${index + 1}`)
              }}
            >
              <span className={isLast ? styles.contextCrumbLast : ''}>{crumb.label}</span>
              {Octicon && <Octicon size={12} className={styles.trailingIcon} />}
            </Breadcrumbs.Item>
          )
        })}
      </Breadcrumbs>
    </FeatureFlags>
  )
}

try{ ContextRegion.displayName ||= 'ContextRegion' } catch {}
try{ Octicon.displayName ||= 'Octicon' } catch {}